import type { Rotation } from "@/shared/state/visualization/schema/v1/three-d";
import type { TopicData } from "@/shared/state/visualization/schema/v1/topic-data";

/** What the type control offers: the rotation modes plus the absence of one. */
export type RotationKind = Rotation["type"] | "none";

export const ROTATION_KINDS: readonly RotationKind[] = [
  "none",
  "euler",
  "axis_angle",
  "quaternion",
];

/** Human labels for the type control, kept beside the values they name. */
export const ROTATION_KIND_LABELS: Record<RotationKind, string> = {
  none: "None",
  euler: "Euler angles",
  axis_angle: "Axis and angle",
  quaternion: "Quaternion",
};

/**
 * One editable channel of a rotation: a constant or a bound topic, and a scale.
 *
 * The same shape for every mode, so the form renders a list rather than three
 * hand-written branches.
 */
export interface RotationChannel {
  /** The schema field this channel writes, e.g. `roll` or `angle`. */
  key: string;
  label: string;
  value: TopicData | number;
  /** Absent for a quaternion's components, which take no scale. */
  scale?: number;
}

/** The channels a mode edits, in the order they should be shown. */
export function channelsOf(rotation: Rotation): RotationChannel[] {
  switch (rotation.type) {
    case "euler":
      return [
        {
          key: "roll",
          label: "Roll (X)",
          value: rotation.roll,
          scale: rotation.rollScale,
        },
        {
          key: "pitch",
          label: "Pitch (Y)",
          value: rotation.pitch,
          scale: rotation.pitchScale,
        },
        {
          key: "yaw",
          label: "Yaw (Z)",
          value: rotation.yaw,
          scale: rotation.yawScale,
        },
      ];
    case "axis_angle":
      return [
        {
          key: "angle",
          label: "Angle",
          value: rotation.angle,
          scale: rotation.angleScale,
        },
      ];
    case "quaternion":
      return [
        { key: "x", label: "X", value: rotation.x },
        { key: "y", label: "Y", value: rotation.y },
        { key: "z", label: "Z", value: rotation.z },
        { key: "w", label: "W", value: rotation.w },
      ];
  }
}

/**
 * A rotation of `kind`, keeping whatever the existing one already said.
 *
 * Switching mode should not silently discard a binding the user made: an angle
 * bound to a topic stays bound when they move from axis-angle to euler, landing
 * on the first channel of the new mode. Anything with no counterpart falls back
 * to the identity rotation for that mode, which is what an entity that has never
 * been rotated should read as.
 */
export function rotationOfKind(
  kind: RotationKind,
  existing: Rotation | undefined,
): Rotation | undefined {
  if (kind === "none") {
    return undefined;
  }
  const carried = existing ? channelsOf(existing) : [];
  const first = carried[0]?.value;
  const firstScale = carried[0]?.scale ?? 1;

  switch (kind) {
    case "euler":
      return {
        type: "euler",
        roll: existing?.type === "euler" ? existing.roll : first ?? 0,
        rollScale: existing?.type === "euler" ? existing.rollScale : firstScale,
        pitch: existing?.type === "euler" ? existing.pitch : 0,
        pitchScale: existing?.type === "euler" ? existing.pitchScale : 1,
        yaw: existing?.type === "euler" ? existing.yaw : 0,
        yawScale: existing?.type === "euler" ? existing.yawScale : 1,
      };
    case "axis_angle":
      return {
        type: "axis_angle",
        // Y is the axis a joint most often turns about, and an all-zero axis is
        // no rotation at all — a worse default than a guess.
        axis: existing?.type === "axis_angle" ? existing.axis : [0, 1, 0],
        angle: first ?? 0,
        angleScale: firstScale,
      };
    case "quaternion":
      return existing?.type === "quaternion"
        ? existing
        : { type: "quaternion", x: 0, y: 0, z: 0, w: 1 };
  }
}

/**
 * `rotation` with one channel replaced.
 *
 * Written generically so the form can drive every mode through one handler; the
 * cast is contained here rather than at each of the eight call sites a
 * per-channel handler would need.
 */
export function withChannel(
  rotation: Rotation,
  key: string,
  value: TopicData | number,
): Rotation {
  return { ...rotation, [key]: value } as Rotation;
}

/** `rotation` with one channel's scale replaced. */
export function withChannelScale(
  rotation: Rotation,
  key: string,
  scale: number,
): Rotation {
  return { ...rotation, [`${key}Scale`]: scale } as Rotation;
}

/** `rotation` with one component of an axis-angle's axis replaced. */
export function withAxisComponent(
  rotation: Rotation,
  index: 0 | 1 | 2,
  value: number,
): Rotation {
  if (rotation.type !== "axis_angle") {
    return rotation;
  }
  const axis: [number, number, number] = [
    rotation.axis[0],
    rotation.axis[1],
    rotation.axis[2],
  ];
  axis[index] = value;
  return { ...rotation, axis };
}
